import React, { useCallback, useMemo } from 'react';
import {
  StyledContentTile,
  ResponsiveResourceTile,
  StyledVerticalContentTile,
  DiscountIcon,
  DiscountIconContainer,
} from './styles';
import AvailableNowLabels from 'components/available-now-block/components/available-now-labels/available-now-labels';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAppInstanceConfigByBuildings,
  selectAppInstanceConfigByLocationId,
} from 'store/app-instance-configs/selectors';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ResourceBookingEnum } from 'store/app-instance-configs/enums';
import { getResourceBuilding } from 'utils/getResourceBuilding';
import { Resource } from 'store/resource/types';
import { toggleResourceFavorite } from 'store/resource-favorite/actions';
import { useOwnerParams } from 'hooks/use-owner-params.hook';
import { ResourceListRow } from './resource-list-row';
import { IntegrationBadge } from 'components/integration-badge';

interface ResourceItemProps {
  resource: Resource;
  /**
   * Draw the redesigned row card instead of the photo tile — `useFloorplanViewState().isRedesignedListEnabled`,
   * the flag alone. Deliberately not the same gate as the List/Floorplan toggle
   * (`isFloorplanAvailable`): the redesigned card ships regardless of whether the current location is
   * native or has a floorplan, the toggle/map is the part conditional on top of that.
   *
   * Passed in rather than read here: a list is many of these, and reading it per card would put a
   * query observer and a url parse on every row for one page-level fact. The lists that render this
   * already know it.
   */
  isRowCard?: boolean;
  onClick?: (resourceUuid: string, locationUuid: string) => void;
}

export const ResourceItem: React.FC<ResourceItemProps> = ({
  resource,
  isRowCard,
  onClick,
}: ResourceItemProps): JSX.Element => {
  const {
    uuid,
    capacity,
    minimum_price,
    image_url: imageUrl,
    name,
    location,
    discounts,
    buildings,
    is_favorite: isFavorite,
    integration,
  } = resource || {};
  const isMobileDevice = useIsSmallViewportWidth();
  const { resourceBookingPortfolioBased, enableFavoriteResourceBookings } = useFlags();
  const dispatch = useDispatch();
  const { ownerType, ownerUuid } = useOwnerParams();
  const appInstanceConfigByLocation = useSelector(selectAppInstanceConfigByLocationId(location?.id));
  const appInstanceConfigByBuilding = useSelector(
    selectAppInstanceConfigByBuildings(buildings?.map(building => building.uuid)),
  );
  const currentAppInstanceConfig = appInstanceConfigByLocation ?? appInstanceConfigByBuilding;
  const isPaidResource = Boolean(minimum_price);
  const shouldShowDiscount = Boolean(discounts?.length);

  const onFavoriteClick = useCallback(() => {
    dispatch(toggleResourceFavorite.request({ ownerType, ownerUuid, resourceUuid: uuid, nextIsFavorite: !isFavorite }));
  }, [dispatch, ownerType, ownerUuid, uuid, isFavorite]);

  const resourceBuildingOptions = useMemo(
    () => ({
      isResourceBookingPortfolioBased: resourceBookingPortfolioBased,
      isEssensysAdapter: currentAppInstanceConfig?.vertical_adapter?.name === ResourceBookingEnum.EssensysAdapter,
    }),
    [currentAppInstanceConfig, resourceBookingPortfolioBased],
  );

  const onTileClick = useCallback(() => {
    onClick(uuid, currentAppInstanceConfig?.location_id);
  }, [onClick, uuid, currentAppInstanceConfig?.location_id]);

  const contentTileProps = {
    className: 'resource-tile',
    title: name,
    subtitle: getResourceBuilding(resource, resourceBuildingOptions),
    imageUrl,
    onClick: onTileClick,
    // Its own entry rather than a child of `AvailableNowLabels`, whose fixed-width box would clip it: the
    // tile's label container wraps, so the badge takes the line after price and capacity when it needs to.
    statusLabels: [
      <AvailableNowLabels key="labels" isPaid={isPaidResource} capacity={Number(capacity)} title={name} />,
      <IntegrationBadge key="integration" integration={integration} />,
    ],
    skeleton: <ResponsiveResourceTile />,
    ...(shouldShowDiscount && {
      additionalImageIcon: (
        <DiscountIconContainer>
          <DiscountIcon data-testid="discount-icon" />
        </DiscountIconContainer>
      ),
    }),
  };

  if (isRowCard) {
    return (
      <ResourceListRow
        name={name}
        subtitle={contentTileProps.subtitle}
        imageUrl={imageUrl}
        capacity={Number(capacity)}
        isPaid={isPaidResource}
        hasDiscount={shouldShowDiscount}
        isFavorite={isFavorite}
        integration={integration}
        onFavoriteClick={enableFavoriteResourceBookings ? onFavoriteClick : undefined}
        onClick={onTileClick}
      />
    );
  }

  return isMobileDevice ? (
    <StyledContentTile {...contentTileProps} />
  ) : (
    <StyledVerticalContentTile {...contentTileProps} />
  );
};
